<template>
  <v-container>
    <div class="header-content">
      <div v-if="provider.logo" class="logo-wrapper">
        <img :src="provider.logo" />
      </div>

      <div class="name-wrapper">
        <h3>{{ provider.contactName }}</h3>
        <h2>{{ provider.companyName }}</h2>
      </div>

      <div class="address-wrapper">
        <span>{{ provider.addressLine_1 }}</span>
        <span>{{ provider.addressLine_2 }}</span>
        <span>
          {{ provider.addressCity }},
          {{ provider.addressState }}
          {{ provider.addressZip }}
        </span>
      </div>
    </div>

    <div class="body-content">
      <h4>About {{ provider.companyName }}</h4>

      <p>
        {{ provider.description }}
      </p>

      <div class="links-wrapper">
        <a
          v-if="!!provider.linkWebsite"
          class="default-btn link small"
          target="_blank"
          :href="provider.linkWebsite">
          Website
        </a>

        <a
          v-if="!!provider.linkLinkedin"
          class="default-btn link small"
          target="_blank"
          :href="provider.linkLinkedin">
          LinkedIn
        </a>

        <a
          v-if="!!provider.email"
          class="default-btn link small"
          :href="`mailto:${provider.email}`">
          {{ provider.email }}
        </a>

        <a
          v-if="!!provider.phone"
          class="default-btn link small"
          :href="`tel:${provider.phone}`">
          {{ provider.phone }}
        </a>
      </div>
    </div>

    <div class="review-content">
      <h2>Customer Reviews</h2>

      <StarRating
        :rating="provider.reviewScore"
        :editable="false"
        justify="flex-start">
        <template #label>
          <span class="rating-label">{{ reviewCountLabel }}</span>
        </template>
      </StarRating>

      <div class="review-grid-wrapper">
        <ProviderReview
          v-for="review in reviews"
          :key="review.id"
          :review="review" />
      </div>

      <button
        v-if="!areMoreReviews"
        class="default-btn link"
        @click="getReviews">
        Show More
      </button>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

import ProviderReview from '../../components/ProviderReview'

export default {
  name: 'ProviderDetails',
  components: {
    ProviderReview,
  },
  props: {
    providerId: String,
  },

  data: () => ({
    provider: {},
    reviews: [],

    totalReviews: 0,
    loading: false,
  }),

  computed: {
    ...mapGetters('feathers', ['apiProviders', 'apiReviewsProviders']),

    reviewCountLabel () {
      const count = this.provider.reviewCount || 0

      return `(${count} review${count === 1 ? '' : 's'})`
    },

    areMoreReviews () {
      return this.reviews.length >= this.totalReviews
    },
  },

  created () {
    this.getProviderDetails()
    this.getReviews()
  },

  methods: {
    async getProviderDetails () {
      try {
        this.provider = await this.apiProviders.get(this.providerId)

        document.title = `Provider Details | ${this.provider.companyName}`
        this.$gtag.pageview({ page_path: document.title })
      } catch (error) {
        this.addErrorAlert('The provider details could not be retrieved. Please try again and reload the page.')
      }
    },

    async getReviews () {
      if (this.loading) return
      this.loading = true

      try {
        const results = await this.apiReviewsProviders.find({
          query: {
            providerId: this.providerId,
            $limit: 5,
            $skip: this.reviews.length,
          },
        })

        this.reviews.push(...results.data)
        this.totalReviews = results.total
      } catch (error) {
        this.addErrorAlert('Sorry, there was an error getting reviews, please reload the page.')
      }

      this.loading = false
    },
  },
}
</script>

<style lang="scss" scoped>
h2, h3, h4 {
  color: $textDarkMuted;
  margin-bottom: 15px;
}

.header-content {
  margin-bottom: 50px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  @include lg {
    flex-direction: row;
  }

  .logo-wrapper {
    max-width: 200px;

    @include lg {
      margin-right: 35px;
    }

    img {
      height: auto;
      max-width: 100%;
    }
  }

  .name-wrapper {
    margin-top: 20px;

    h3 {
      margin-bottom: 5px;
      font-family: "Sansation Regular", sans-serif;
      font-weight: normal;
    }
  }

  .address-wrapper {
    margin-top: 20px;

    @include lg {
      margin-left: auto;
    }

    span {
      display: block;

      color: $textDarkMuted;
      font-family: "Avenir Book", sans-serif;
      font-size: 16px;
      line-height: 20px;
      text-align: left;

      @include lg {
        text-align: right;
      }
    }
  }
}

.body-content {
  margin-bottom: 60px;

  h4 {
    font-family: "Sansation Regular", sans-serif;
    font-weight: normal;
  }

  p {
    color: black;
  }

  .links-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    @include md {
      flex-direction: row;
      align-items: center;
    }

    a {
      display: flex;
      position: relative;
      margin: 0 5px 10px !important;
      padding: 0 10px !important;
      min-height: 0;

      &:not(:first-child):before {
        content: '';
        position: absolute;
        background-color: #D5D5D5;

        @include smAndBelow {
          top: -8px;
          left: 10px;
          height: 2px;
          width: 30px;
        }

        @include md {
          top: 0;
          bottom: 0;
          left: -4px;
          width: 2px;
        }
      }
    }
  }
}

.review-content {
  margin-bottom: 36px;

  .rating-wrapper {
    flex-shrink: 0;
    span { padding-left: 10px; }
  }

  .rating-label {
    margin-left: 5px;

    color: #908E8E;
    font-family: "Sansation Regular", sans-serif;
    font-size: 14px;
    line-height: 15px;
  }
}

.review-grid-wrapper {
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 20px;

  margin: 30px 0 20px;

  & > * {
    border-top: 1px solid #CBCBCB;

    &:last-child {
      border-bottom: 1px solid #CBCBCB;
    }
  }
}
</style>
